"use client";

import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AnimateOnScroll } from "@/components/home/animate-on-scroll";
import { EmailResultsModal, type EmailSummary } from "@/components/calculators/email-results-modal";

export function NumField({
  id, label, value, onChange, placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} style={{ color: "rgba(255,255,255,0.85)" }}>{label}</Label>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        pattern="[0-9]*"
        min={0}
        step="any"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10"
      />
    </div>
  );
}

export function ResultPanel({
  title, stat, unit, rows, footnote,
}: {
  title: string;
  stat: string;
  unit: string;
  rows: { label: string; value: string }[];
  footnote: string;
}) {
  return (
    <div style={{ background: "rgba(255,255,255,0.06)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 14, overflow: "hidden", padding: "24px", color: "white", boxShadow: "0 4px 24px rgba(0,0,0,0.4)" }}>
      <p className="text-xs font-semibold uppercase tracking-widest text-[#D4A84B]">{title}</p>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="text-6xl font-bold tabular-nums">{stat}</span>
        <span className="text-sm uppercase tracking-wider" style={{ color: "#d1d5db" }}>{unit}</span>
      </div>
      <dl className="mt-6 space-y-2 border-t border-white/10 pt-4 text-sm">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between">
            <dt style={{ color: "#d1d5db" }}>{r.label}</dt>
            <dd className="font-semibold tabular-nums" style={{ color: "#f3f4f6" }}>{r.value}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-6 border-t border-white/10 pt-4 text-xs leading-relaxed" style={{ color: "#9ca3af" }}>{footnote}</p>
    </div>
  );
}

export function BigStat({
  label, stat, sub, highlight,
}: {
  label: string;
  stat: string | number;
  sub?: string;
  highlight?: boolean;
}) {
  return (
    <div>
      <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: highlight ? "#D4A84B" : "rgba(255,255,255,0.6)" }}>{label}</p>
      <p style={{ fontSize: 32, fontWeight: 700, color: highlight ? "#D4A84B" : "#ffffff", fontVariantNumeric: "tabular-nums", lineHeight: 1.1, marginTop: 2 }}>{stat}</p>
      {sub && <p style={{ fontSize: 11, color: "rgba(255,255,255,0.70)", marginTop: 2 }}>{sub}</p>}
    </div>
  );
}

export function ZoneBlock({
  title, rule, a, b, metric,
}: {
  title: string;
  rule: string;
  a: React.ReactNode;
  b: React.ReactNode;
  metric: string;
}) {
  return (
    <div style={{ background: "rgba(255,255,255,0.06)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 12, padding: "16px 18px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
        <h4 style={{ fontSize: 14, fontWeight: 700, color: "#ffffff", textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</h4>
        <span style={{ fontSize: 12, color: "#D4A84B", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{metric}</span>
      </div>
      <p style={{ fontSize: 11, color: "rgba(255,255,255,0.70)", marginBottom: 10 }}>{rule}</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>{a}{b}</div>
    </div>
  );
}

export function EmailButton({ summary }: { summary: EmailSummary }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{ marginTop: 16, display: "inline-flex", alignItems: "center", gap: 8, background: "#DC2626", color: "#fff", border: "none", borderRadius: 999, padding: "9px 18px", fontSize: 13, fontWeight: 700, letterSpacing: "0.02em", cursor: "pointer" }}
      >
        ✉ Email Results
      </button>
      <EmailResultsModal open={open} onClose={() => setOpen(false)} summary={summary} />
    </>
  );
}

export function CalcSection({
  id, icon, title, subtitle, children,
}: {
  id: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} style={{ padding: "0 0 32px", scrollMarginTop: "112px" }}>
      <AnimateOnScroll>
        <div style={{ background: "rgba(5,13,24,0.85)", borderRadius: "20px", border: "1px solid rgba(255,255,255,0.15)", borderTop: "3px solid #B88A2F", boxShadow: "0 4px 32px rgba(0,0,0,0.5)", padding: "32px 32px 40px" }}>
          <div className="flex items-start gap-3 mb-6">
            {icon}
            <div>
              <h2 className="text-xl font-bold" style={{ color: "#ffffff", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</h2>
              <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.72)", lineHeight: 1.6 }}>{subtitle}</p>
            </div>
          </div>
          {children}
        </div>
      </AnimateOnScroll>
    </section>
  );
}
